import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styles from './Withdraw.module.sass'
import { fetchWallets } from 'store/actions'
import ConfirmWithdrawModal from './ConfirmWithdrawModal'
import SecurityAuthModal from './SecurityAuthModal'

const networks = [
  {
    id: 'trc20',
    name: 'TRC20',
    chain: 'Tron',
    fee: '1.5 USDT',
    minimum: '1 USDT',
    arrival: '~ 3 min',
  },
  {
    id: 'erc20',
    name: 'ERC20',
    chain: 'Ethereum',
    fee: '2.204731 USDT (~ 2.20 USD)',
    minimum: '1 USDT',
    arrival: '~ 3 min',
  },
  {
    id: 'solana',
    name: 'SOLANA',
    chain: '',
    fee: '0.8358 USDT (~ 0.83 USD)',
    minimum: '2 USDT',
    arrival: '~ 3 min',
  },
  {
    id: 'bep20',
    name: 'BEP20',
    chain: 'BNB Smart Chain',
    fee: '1.5 USDT (~ 1.50 USD)',
    minimum: '1 USDT',
    arrival: '~ 3 min',
  },
  {
    id: 'apt',
    name: 'APT',
    chain: 'Aptos',
    fee: '0 USDT Free',
    minimum: '10 USDT',
    arrival: '~ 2 min',
  },
  {
    id: 'avax',
    name: 'AVAXCCHAIN',
    chain: '',
    fee: '0.6 USDT (~ 0.60 USD)',
    minimum: '1 USDT',
    arrival: '~ 2 min',
  },
  {
    id: 'near',
    name: 'NEAR',
    chain: 'NEAR Protocol',
    fee: '0.3 USDT (~ 0.30 USD)',
    minimum: '1 USDT',
    arrival: '~ 2 min',
  },
  {
    id: 'celo',
    name: 'CELO',
    chain: '',
    fee: '0.03 USDT (~ 0.030 USD)',
    minimum: '10 USDT',
    arrival: '~ 2 min',
  },
]

const notes = [
  'We recommend enabling Express Withdrawal for your addresses on HTX and enjoy zero service fee.',
  'Min Withdrawal Amount (excluding fee): 1 USDT (TRC20).',
  'To ensure the safety of your funds, your withdrawal request may require account verification or password confirmation.',
]

const formatNetworkTitle = ({ name, chain }) =>
  chain ? `${name} (${chain})` : name

const WithdrawOverview = () => {
  const navigate = useNavigate()
  const { exchangeWallets = [] } = useSelector((state) => state?.walletReducer)
  const { referenceCurrency } = useSelector(state => state.assets)
  const [selectedWallet, setSelectedWallet] = useState(null)
  const [address, setAddress] = useState('')
  const [amount, setAmount] = useState('')
  const [visibleConfirmModal, setVisibleConfirmModal] = useState(false)
  const [visibleSecurityModal, setVisibleSecurityModal] = useState(false)
  const [isRiskAccepted, setIsRiskAccepted] = useState(false)
  const [emailCode, setEmailCode] = useState('')
  const [gaCode, setGaCode] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('')
  const selectedNetworkData = networks.find((network) => network.id === selectedNetwork)
  const feeValue = selectedNetworkData ? Number(selectedNetworkData.fee.split(' ')[0]) || 0 : 0
  const amountValue = Number(amount) || 0
  const receiveValue = Math.max(amountValue - feeValue, 0)
  const minimumAmount = selectedNetworkData
    ? Number(selectedNetworkData.minimum.split(' ')[0]) || 0
    : 0
  const isWithdrawEnabled =
    Boolean(selectedWallet) &&
    Boolean(selectedNetwork) &&
    address.trim().length > 0 &&
    amount !== '' &&
    amountValue > 0 &&
    amountValue >= minimumAmount;
  // const fakeWallets = [
  //   {
  //     _id: 'wallet1',
  //     asset: 'USDT',
  //     balance: '1000',
  //     subUid: "213213",
  //     assetId: {
  //       symbol: 'USDT'
  //     }
  //   },
  //   {
  //     _id: 'wallet2',
  //     asset: 'TRC20',
  //     balance: '200',
  //     assetId: {
  //       symbol: 'TRC20'
  //     },
  //     subUid: "213214"
  //   },
  // ]
  const wallets = useMemo(
    () =>
      exchangeWallets.filter(
        (wallet) => wallet?.subUid && Number(wallet?.balance) > 0
      ),
    [exchangeWallets]
  )

  const handleOpenConfirmModal = () => {
    if (!isWithdrawEnabled) return
    setIsRiskAccepted(false)
    setVisibleConfirmModal(true)
  }

  const handleOpenSecurityModal = () => {
    if (!isRiskAccepted) return
    setVisibleConfirmModal(false)
    setEmailCode('')
    setGaCode('')
    setVisibleSecurityModal(true)
  }

  const handleCloseSecurityModal = () => {
    setVisibleSecurityModal(false)
  }

  const handleSubmitSecurityForm = () => {
    setVisibleSecurityModal(false)
    navigate('/withdraw-details/sadasdasd')
  }

  return (
    <>
      <div className={styles.page}>
        <div className={styles.panel}>
          <div className={styles.columns}>
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <span className={styles.step}>1</span>
                <h2 className={styles.title}>Select wallet</h2>
              </div>

              <div className={styles.selectWrap}>
                <select
                  className={styles.coinSelect}
                  value={selectedWallet?._id}
                  onChange={(e) => {
                    const wallet = wallets.find(w => w._id === e.target.value);
                    const selectedCurrency = referenceCurrency?.data?.find(
                      curr => curr?.currency?.toUpperCase() === wallet?.asset?.toUpperCase()
                    );
                    const networks = !selectedCurrency ? [] : selectedCurrency?.chains?.map(network => ({
                      _id: `${network?.displayName}-${network?.chain}-${Math.floor(Math.random() * 1000)}`,
                      name: network?.displayName,
                      chainId: {
                        _id: `${network?.displayName}-${network?.chain}-${Math.floor(Math.random() * 1000)}`,
                        name: network?.displayName,
                        cryptoapiName: network?.chain,
                        ...network
                      },
                      address: network?.address || ''
                    }))
                    setSelectedWallet({
                      ...wallet,
                      networks
                    });
                  }}
                >
                  {wallets.length === 0 ? (
                    <option value="">No wallets available</option>
                  ) : (
                    <>
                      <option value="">Select Wallet</option>
                      {wallets.map((wallet) => {
                        const walletId = wallet._id || wallet.subUid
                        const walletSymbol =
                          wallet?.assetId?.symbol || wallet?.asset?.toUpperCase()

                        return (
                          <option key={walletId} value={walletId}>
                            {walletSymbol} - Balance: {wallet?.balance}
                          </option>
                        )
                      })}
                    </>
                  )}
                </select>
                <span className={styles.chevron} />
              </div>
              {selectedWallet && (
                <>

                  <div className={styles.coinCard}>
                    <div className={styles.infoRow}>
                      <span>Selected wallet</span>
                      <strong>{selectedWallet?.asset?.toUpperCase() || 'Wallet'}</strong>
                    </div>
                    <div className={styles.infoRow}>
                      <span>Available balance</span>
                      <strong>{selectedWallet?.balance || 0}</strong>
                    </div>
                    <div className={styles.infoRow}>
                      <span>Wallet ID</span>
                      <strong>{selectedWallet?._id}</strong>
                    </div>
                  </div>

                  <div className={styles.balanceRow}>
                    <div>
                      <p className={styles.balanceLabel}>Withdrawable</p>
                      <strong className={styles.balanceValue}>
                        {selectedWallet?.balance || 0} {selectedWallet?.asset?.toUpperCase() || "Wallet"}
                      </strong>
                    </div>
                    <button type="button" className={styles.linkButton}>
                      Apply for increase amount
                    </button>
                  </div>
                </>
              )}

              <div className={styles.notes}>
                {notes.map((note) => (
                  <p key={note} className={styles.note}>
                    {note}
                  </p>
                ))}
              </div>
            </section>

            {!selectedWallet ? null : (
              <section className={styles.section}>
                <div className={styles.sectionHeader}>
                  <span className={styles.step}>2</span>
                  <h2 className={styles.title}>Withdrawal Details</h2>
                </div>

                <p className={styles.label}>Withdrawal Network</p>
                <div className={styles.networkGrid}>
                  {selectedWallet.networks.map((network) => {
                    const active = network._id === selectedNetwork

                    return (
                      <button
                        key={network._id}
                        type="button"
                        className={`${styles.networkCard} ${active ? styles.networkCardActive : ''}`}
                        onClick={() => setSelectedNetwork(network._id)}
                      >
                        <div className={styles.networkHead}>
                          <span className={styles.radio}>
                            <span className={styles.radioDot} />
                          </span>
                          <span className={styles.networkTitle}>
                            {formatNetworkTitle(network)}
                          </span>
                        </div>
                        <p className={styles.networkText}>Fee {network?.fee}</p>
                        <p className={styles.networkText}>
                          Minimum Withdrawal {network?.minimum}
                        </p>
                        <p className={styles.networkText}>Arrival Time {network?.arrival}</p>
                      </button>
                    )
                  })}
                </div>
                <div className={styles.formBlock}>
                  <div className={styles.fieldBlock}>
                    <div className={styles.fieldHeader}>
                      <span className={styles.fieldLabel}>Address</span>
                      <button type="button" className={styles.fieldLink}>
                        Withdrawal Address
                      </button>
                    </div>
                    <div className={styles.inputShell}>
                      <input
                        type="text"
                        className={styles.textInput}
                        placeholder="Enter withdrawal address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                      />
                      <div className={styles.inputActions}>
                        <button
                          type="button"
                          className={styles.clearAction}
                          onClick={() => setAddress('')}
                        >
                          x
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className={styles.fieldBlock}>
                    <span className={styles.fieldLabel}>Withdrawal Amount</span>
                    <div className={styles.amountShell}>
                      <input
                        type="number"
                        min={minimumAmount}
                        className={styles.amountInput}
                        placeholder={selectedNetworkData ? `MIN ${minimumAmount}` : 'MIN 0'}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                      />
                      <span className={styles.amountUnit}>
                        {selectedWallet?.asset?.toUpperCase() || 'USDT'}
                      </span>
                      <button
                        type="button"
                        className={styles.amountLink}
                        onClick={() => setAmount(selectedWallet?.balance || '')}
                      >
                        All
                      </button>
                    </div>
                  </div>

                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>Fee</span>
                    <strong className={styles.summaryValue}>
                      {selectedNetworkData ? `>= ${selectedNetworkData.fee}` : '--'}
                    </strong>
                  </div>

                  <div className={styles.receiveRow}>
                    <span className={styles.summaryLabel}>You Receive</span>
                    <strong className={styles.receiveValue}>
                      {receiveValue.toFixed(2)} {selectedWallet?.asset?.toUpperCase() || 'USDT'}
                    </strong>
                  </div>

                  <button
                    type="button"
                    className={`${styles.withdrawButton} ${isWithdrawEnabled ? styles.withdrawButtonActive : ''}`}
                    disabled={!isWithdrawEnabled}
                    onClick={handleOpenConfirmModal}
                  >
                    Withdraw
                  </button>
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
      <ConfirmWithdrawModal
        visible={visibleConfirmModal}
        onClose={() => setVisibleConfirmModal(false)}
        address={address}
        assetSymbol={selectedWallet?.asset?.toUpperCase() || 'USDT'}
        receiveValue={receiveValue}
        amountValue={amountValue}
        feeValue={feeValue}
        isRiskAccepted={isRiskAccepted}
        onRiskChange={setIsRiskAccepted}
        onConfirm={handleOpenSecurityModal}
      />
      <SecurityAuthModal
        visible={visibleSecurityModal}
        onClose={handleCloseSecurityModal}
        onSubmit={handleSubmitSecurityForm}
        emailCode={emailCode}
        gaCode={gaCode}
        onEmailCodeChange={setEmailCode}
        onGaCodeChange={setGaCode}
      />
    </>
  )
}

export default WithdrawOverview
